import type { FmMapWorkspaceDescriptor } from "./FmMapWorkspaceRoot.types";
import {
  validateMapWorkspaceActionShape,
} from "./FmMapWorkspaceRoot.shape";
import {
  assertMapWorkspaceExactObject as exactObject,
  requireMapWorkspaceFields as requireFields,
  requireMapWorkspaceText as requireText,
  uniqueMapWorkspaceIds as uniqueIds,
} from "./FmMapWorkspaceValidation.shared";

function validateInspectionShape(
  inspection: NonNullable<FmMapWorkspaceDescriptor["inspection"]>,
): void {
  exactObject(
    inspection,
    ["ariaLabel", "title", "closeLabel", "openFullLabel", "values", "actions", "hazards"],
    "inspection",
  );
  requireFields(
    inspection,
    ["ariaLabel", "title", "closeLabel", "values"],
    "inspection",
  );
  inspection.values.forEach((item, index) => {
    exactObject(
      item,
      ["id", "label", "value", "unavailable"],
      `inspection.values[${index}]`,
    );
    requireFields(
      item,
      ["id", "label", "value", "unavailable"],
      `inspection.values[${index}]`,
    );
  });
  (inspection.actions ?? []).forEach((action, index) => {
    validateMapWorkspaceActionShape(
      action,
      `inspection.actions[${index}]`,
    );
    requireFields(
      action,
      ["id", "label", "intent"],
      `inspection.actions[${index}]`,
    );
  });
  if (!inspection.hazards) return;
  exactObject(
    inspection.hazards,
    ["ariaLabel", "eyebrow", "items"],
    "inspection.hazards",
  );
  requireFields(
    inspection.hazards,
    ["ariaLabel", "eyebrow", "items"],
    "inspection.hazards",
  );
  inspection.hazards.items.forEach((item, index) => {
    exactObject(item, ["id", "label"], `inspection.hazards.items[${index}]`);
    requireFields(item, ["id", "label"], `inspection.hazards.items[${index}]`);
  });
}

/** Validates the selection-owned inspector capability at the sealed boundary. */
export function validateMapWorkspaceInspection(
  descriptor: FmMapWorkspaceDescriptor,
): void {
  const inspection = descriptor.inspection;
  const selectionOwnsInspection =
    descriptor.selection.kind === "stand" ||
    descriptor.selection.kind === "campaign" ||
    descriptor.selection.kind === "plot" ||
    descriptor.selection.kind === "risk";
  if (inspection === null) {
    if (selectionOwnsInspection) {
      throw new Error(
        "Invalid map workspace descriptor: inspectable selections require an inspection descriptor.",
      );
    }
    return;
  }
  if (!selectionOwnsInspection) {
    throw new Error(
      "Invalid map workspace descriptor: inspection requires an inspectable selection.",
    );
  }
  validateInspectionShape(inspection);
  Object.entries({
    ariaLabel: inspection.ariaLabel,
    title: inspection.title,
    closeLabel: inspection.closeLabel,
  }).forEach(([key, value]) => requireText(value, `inspection.${key}`));
  if (inspection.openFullLabel !== undefined) {
    requireText(inspection.openFullLabel, "inspection.openFullLabel");
  }
  uniqueIds(inspection.values, "inspection.values");
  inspection.values.forEach((item) => {
    requireText(item.label, `inspection value "${item.id}".label`);
    requireText(item.value, `inspection value "${item.id}".value`);
    if (typeof item.unavailable !== "boolean") {
      throw new Error(
        `Invalid map workspace descriptor: inspection value "${item.id}".unavailable must be boolean.`,
      );
    }
  });
  uniqueIds(inspection.actions ?? [], "inspection.actions");
  (inspection.actions ?? []).forEach((action) => {
    requireText(action.label, `inspection action "${action.id}".label`);
    if (action.disabledReason !== undefined) {
      requireText(
        action.disabledReason,
        `inspection action "${action.id}".disabledReason`,
      );
    }
  });
  if (!inspection.hazards) return;
  requireText(inspection.hazards.ariaLabel, "inspection.hazards.ariaLabel");
  requireText(inspection.hazards.eyebrow, "inspection.hazards.eyebrow");
  uniqueIds(inspection.hazards.items, "inspection.hazards.items");
  inspection.hazards.items.forEach((item) =>
    requireText(item.label, `inspection hazard "${item.id}".label`),
  );
}
